const collectionRenderEvent = 'collection:render';
const collectionProductsUpdatedEvent = 'collection:products-updated';
const collectionInfiniteStatePrefix = 'collectionInfiniteState:v2:';
const legacyCollectionInfiniteStatePrefix = 'collectionInfiniteState:';

function getMotionSafeScrollBehavior(behavior = 'smooth') {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : behavior;
}

function dispatchCollectionRender(detail) {
  document.dispatchEvent(new CustomEvent(collectionRenderEvent, { detail }));
}

function dispatchCollectionProductsUpdated() {
  document.dispatchEvent(new CustomEvent(collectionProductsUpdatedEvent));
}

function getGridColumnCount(grid) {
  const columns = window.getComputedStyle(grid).gridTemplateColumns;

  if (!columns || columns === 'none') return 1;

  return Math.max(1, columns.split(' ').filter(Boolean).length);
}

function updateCollectionProductAnimationIndexes(scope = document) {
  const productLists = scope.querySelectorAll
    ? scope.querySelectorAll('.collection-main .product-list')
    : [];

  productLists.forEach((productList) => {
    const columnCount = getGridColumnCount(productList);

    productList.querySelectorAll('.product-item').forEach((item, index) => {
      item.style.setProperty('--animation-index', index % columnCount);
    });
  });
}

function setProductListLoading(isLoading) {
  const productList = document.querySelector('.product-list');

  if (productList) {
    productList.style.opacity = isLoading ? '0.5' : '1';
  }
}

function getCollectionInfiniteStateKey(url = window.location.href) {
  return `${collectionInfiniteStatePrefix}${url}`;
}

function clearLegacyCollectionInfiniteState() {
  try {
    Object.keys(sessionStorage).forEach((key) => {
      if (key.startsWith(legacyCollectionInfiniteStatePrefix) && !key.startsWith(collectionInfiniteStatePrefix)) {
        sessionStorage.removeItem(key);
      }
    });
  } catch (error) {
    console.error('Failed to clear legacy collection state:', error);
  }
}

function getCollectionInfinitePaginationHtml() {
  return document.querySelector('collection-infinite-pagination')?.outerHTML || '';
}

function shouldSaveCollectionInfiniteState(url = window.location.href) {
  return new URL(url).searchParams.has('page');
}

function saveCollectionInfiniteState(url = window.location.href) {
  if (!shouldSaveCollectionInfiniteState(url)) return;

  const productList = document.querySelector('.product-list');

  if (!productList) return;

  try {
    sessionStorage.setItem(
      getCollectionInfiniteStateKey(url),
      JSON.stringify({
        productListHtml: productList.outerHTML,
        paginationHtml: getCollectionInfinitePaginationHtml(),
        scrollY: window.scrollY,
      })
    );
  } catch (error) {
    console.error('Failed to save collection state:', error);
  }
}

function restoreCollectionInfiniteState() {
  clearLegacyCollectionInfiniteState();

  const productList = document.querySelector('.product-list');

  if (!productList) return false;

  try {
    const state = JSON.parse(sessionStorage.getItem(getCollectionInfiniteStateKey()) || 'null');

    if (!state?.productListHtml) return false;

    productList.outerHTML = state.productListHtml;

    const currentPagination = document.querySelector('collection-infinite-pagination');

    if (state.paginationHtml) {
      if (currentPagination) {
        currentPagination.outerHTML = state.paginationHtml;
      } else {
        document.querySelector('.product-list')?.insertAdjacentHTML('afterend', state.paginationHtml);
      }
    } else {
      currentPagination?.remove();
    }

    window.requestAnimationFrame(() => {
      window.scrollTo({ top: state.scrollY || 0, behavior: 'auto' });
    });

    dispatchCollectionProductsUpdated();

    return true;
  } catch (error) {
    console.error('Failed to restore collection state:', error);
    sessionStorage.removeItem(getCollectionInfiniteStateKey());
    return false;
  }
}

async function fetchCollectionDoc(url, errorMessage) {
  const response = await fetch(url.toString());

  if (!response.ok) {
    throw new Error(`${errorMessage}: ${response.status}`);
  }

  const html = await response.text();

  return new DOMParser().parseFromString(html, 'text/html');
}

function updateCollectionCount(doc) {
  const currentCount = document.querySelector('.collection-utilities-count');
  const newCount = doc.querySelector('.collection-utilities-count');

  if (currentCount && newCount) {
    currentCount.textContent = newCount.textContent;
  }
}

function scrollToCollectionMain() {
  const collectionMain = document.querySelector('.collection-main');

  if (!collectionMain) return;

  const headerGroup = document.querySelector('#header-group');
  const headerHeight = headerGroup ? headerGroup.clientHeight : 0;
  const collectionTop = collectionMain.getBoundingClientRect().top + window.scrollY;
  const isBelowCollection = window.scrollY > collectionTop;
  const top = collectionTop - (isBelowCollection ? headerHeight : 0);

  window.scrollTo({
    top,
    behavior: getMotionSafeScrollBehavior(),
  });
}

function preserveSearchParams(sourceUrl, targetUrl) {
  ['q', 'type', 'options[prefix]'].forEach((name) => {
    const value = sourceUrl.searchParams.get(name);

    if (value) {
      targetUrl.searchParams.set(name, value);
    }
  });
}

let collectionAnimationIndexFrame = null;

function requestCollectionProductAnimationIndexUpdate() {
  if (collectionAnimationIndexFrame) return;

  collectionAnimationIndexFrame = window.requestAnimationFrame(() => {
    collectionAnimationIndexFrame = null;
    updateCollectionProductAnimationIndexes();
  });
}

updateCollectionProductAnimationIndexes();
window.addEventListener('resize', requestCollectionProductAnimationIndexUpdate);
document.addEventListener(collectionProductsUpdatedEvent, requestCollectionProductAnimationIndexUpdate);

function initCollectionUtilitiesStickyState() {
  const utilitiesBars = document.querySelectorAll('.collection-utilities');

  if (!utilitiesBars.length) return;

  let ticking = false;

  const update = () => {
    utilitiesBars.forEach((bar) => {
      const top = parseFloat(getComputedStyle(bar).top) || 0;
      const isStuck = bar.getBoundingClientRect().top <= top + 1;

      bar.dataset.stuck = isStuck ? 'true' : 'false';
    });

    ticking = false;
  };

  const requestUpdate = () => {
    if (ticking) return;

    ticking = true;
    window.requestAnimationFrame(update);
  };

  update();
  window.addEventListener('scroll', requestUpdate, { passive: true });
  window.addEventListener('resize', requestUpdate);
}

initCollectionUtilitiesStickyState();

function replaceProductList(doc) {
  const currentProductList = document.querySelector('.product-list');
  const newProductList = doc.querySelector('.product-list');

  if (!currentProductList || !newProductList) return;

  currentProductList.outerHTML = newProductList.outerHTML;
  dispatchCollectionProductsUpdated();
  scrollToCollectionMain();
}

function updateStaticPagination(doc) {
  if (document.querySelector('collection-infinite-pagination')) return;

  const currentPagination = document.querySelector('.pagination-wrapper');
  const newPagination = doc.querySelector('.pagination-wrapper');

  if (currentPagination && newPagination) {
    currentPagination.outerHTML = newPagination.outerHTML;
  } else if (currentPagination) {
    currentPagination.remove();
  } else if (newPagination) {
    document.querySelector('.product-list')?.after(newPagination);
  }
}

document.addEventListener(collectionRenderEvent, (event) => {
  updateStaticPagination(event.detail.doc);
});

function initCollectionStickySidebar() {
  const sidebar = document.querySelector('.collection-main-left');

  if (!sidebar) return;

  const headerGroup = document.querySelector('#header-group');
  const desktopQuery = window.matchMedia('(min-width: 1200px)');
  const stickyOffset = 20;
  let ticking = false;

  const getHeaderHeight = () => headerGroup ? headerGroup.clientHeight : 0;
  const setSidebarTop = (top) => {
    sidebar.style.top = `${top}px`;
  };
  const getStickyTop = () => {
    if (!headerGroup || headerGroup.classList.contains('hide')) {
      return stickyOffset;
    }

    return getHeaderHeight() + stickyOffset;
  };

  const applyStickyState = () => {
    if (!desktopQuery.matches) {
      sidebar.style.position = '';
      sidebar.style.alignSelf = '';
      sidebar.style.top = '';
      return;
    }

    sidebar.style.position = 'sticky';
    sidebar.style.alignSelf = 'start';
    setSidebarTop(getStickyTop());
  };

  const handleMediaChange = () => {
    applyStickyState();
  };

  const handleScroll = () => {
    if (!desktopQuery.matches) return;
    if (ticking) return;

    ticking = true;

    window.requestAnimationFrame(() => {
      setSidebarTop(getStickyTop());
      ticking = false;
    });
  };

  applyStickyState();
  desktopQuery.addEventListener('change', handleMediaChange);
  window.addEventListener('resize', applyStickyState);
  window.addEventListener('scroll', handleScroll, { passive: true });
}

initCollectionStickySidebar();

class CollectionFilters extends HTMLElement {
  constructor() {
    super();
    this.priceDebounceTimer = null;
    this.announcementTimer = null;
    this.clearAnnouncementTimer = null;
    this.handleChange = this.handleChange.bind(this);
    this.handleInput = this.handleInput.bind(this);
    this.handleCollectionRender = this.handleCollectionRender.bind(this);
    this.handleViewResultsClick = this.handleViewResultsClick.bind(this);
    this.handleClearFiltersClick = this.handleClearFiltersClick.bind(this);
  }

  connectedCallback() {
    this.addEventListener('change', this.handleChange);
    this.addEventListener('input', this.handleInput);
    document.addEventListener(collectionRenderEvent, this.handleCollectionRender);
    this.initPriceRanges();
    this.initDialogFooterButtons();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.handleChange);
    this.removeEventListener('input', this.handleInput);
    document.removeEventListener(collectionRenderEvent, this.handleCollectionRender);
    this.viewResultsButton?.removeEventListener('click', this.handleViewResultsClick);
    this.clearFiltersButton?.removeEventListener('click', this.handleClearFiltersClick);
    clearTimeout(this.priceDebounceTimer);
    clearTimeout(this.announcementTimer);
    clearTimeout(this.clearAnnouncementTimer);
  }

  handleChange(event) {
    const target = event.target;

    if (!(target instanceof HTMLInputElement)) return;
    if (target.closest('.collection-filter-price-range')) return;
    if (target.type !== 'checkbox' && target.type !== 'radio') return;

    this.updateCollection();
  }

  handleInput(event) {
    const target = event.target;

    if (!(target instanceof HTMLInputElement)) return;
    if (!target.closest('.collection-filter-price-range')) return;

    if (target.matches('[data-range-type]')) {
      this.syncPriceRangeInput(target);
    } else {
      this.syncPriceTextInput(target);
    }

    clearTimeout(this.priceDebounceTimer);

    this.priceDebounceTimer = setTimeout(() => {
      this.updateCollection();
    }, 800);
  }

  handleCollectionRender(event) {
    if (event.detail.source !== 'filters') return;

    this.updateFromDoc(event.detail.doc);
  }

  initPriceRanges() {
    this.querySelectorAll('.collection-filter-price-range').forEach((priceRange) => {
      this.syncPriceRange(priceRange, null, false);
    });
  }

  initDialogFooterButtons() {
    const dialog = this.closest('#collection-filters-dialog');

    if (!dialog) return;

    this.viewResultsButton = dialog.querySelector('[data-btn-view-results]');
    this.clearFiltersButton = dialog.querySelector('[data-btn-clear-filters]');

    this.viewResultsButton?.addEventListener('click', this.handleViewResultsClick);
    this.clearFiltersButton?.addEventListener('click', this.handleClearFiltersClick);
  }

  isInDialog() {
    return Boolean(this.closest('#collection-filters-dialog'));
  }

  handleViewResultsClick() {
    this.closeDialog();
  }

  handleClearFiltersClick() {
    this.clearFilters();
    this.updateCollection(this.getClearFiltersUrl());
  }

  getDialogHost() {
    return this.closest('theme-dialog');
  }

  closeDialog() {
    this.getDialogHost()?.requestClose();
  }

  clearFilters() {
    this.querySelectorAll('input[type="checkbox"], input[type="radio"]').forEach((input) => {
      input.checked = false;
    });

    this.querySelectorAll('.collection-filter-price-inputs input').forEach((input) => {
      input.value = '';
    });

    this.initPriceRanges();
  }

  getClearFiltersUrl() {
    const currentUrl = new URL(window.location.href);
    const url = new URL(window.location.pathname, window.location.origin);
    const currentSort = currentUrl.searchParams.get('sort_by');

    preserveSearchParams(currentUrl, url);

    if (currentSort) {
      url.searchParams.set('sort_by', currentSort);
    }

    return url;
  }

  getPriceRangeParts(priceRange) {
    const minRange = priceRange.querySelector('[data-range-type="min"]');
    const maxRange = priceRange.querySelector('[data-range-type="max"]');
    const textInputs = priceRange.querySelectorAll('.collection-filter-price-inputs input');

    return {
      minInput: textInputs[0],
      maxInput: textInputs[1],
      minRange,
      maxRange,
    };
  }

  getNumber(value, fallback = 0) {
    if (value === null || value === undefined || value === '') return fallback;

    const number = Number(String(value).replace(',', '.'));

    return Number.isNaN(number) ? fallback : number;
  }

  getInputNumber(input, fallback = 0) {
    if (!input) return fallback;

    return this.getNumber(input.value, fallback);
  }

  clampPriceValue(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  syncPriceRangeInput(input) {
    const priceRange = input.closest('.collection-filter-price-range');

    if (!priceRange) return;

    this.syncPriceRange(priceRange, input.dataset.rangeType, true);
  }

  syncPriceTextInput(input) {
    const priceRange = input.closest('.collection-filter-price-range');

    if (!priceRange) return;

    this.syncPriceRange(priceRange, null, false);
  }

  syncPriceRange(priceRange, activeRangeType = null, updateTextInputs = false) {
    const { minInput, maxInput, minRange, maxRange } = this.getPriceRangeParts(priceRange);

    if (!minInput || !maxInput || !minRange || !maxRange) return;

    const minAllowed = this.getNumber(minRange.min, 0);
    const maxAllowed = this.getNumber(maxRange.max, this.getNumber(minRange.max, 0));
    const minInputHasValue = minInput.value.trim() !== '';
    const maxInputHasValue = maxInput.value.trim() !== '';
    const minFallback = minInputHasValue ? this.getInputNumber(minInput, minAllowed) : minAllowed;
    const maxFallback = maxInputHasValue ? this.getInputNumber(maxInput, maxAllowed) : maxAllowed;

    let minValue = activeRangeType === 'min'
      ? this.getInputNumber(minRange, minFallback)
      : this.getInputNumber(minInput, minFallback);
    let maxValue = activeRangeType === 'max'
      ? this.getInputNumber(maxRange, maxFallback)
      : this.getInputNumber(maxInput, maxFallback);

    minValue = this.clampPriceValue(minValue, minAllowed, maxAllowed);
    maxValue = this.clampPriceValue(maxValue, minAllowed, maxAllowed);

    if (minValue > maxValue) {
      if (activeRangeType === 'min') {
        maxValue = minValue;
      } else {
        minValue = maxValue;
      }
    }

    minRange.value = minValue;
    maxRange.value = maxValue;
    minRange.max = maxValue;
    maxRange.min = minValue;

    if ((updateTextInputs && activeRangeType === 'min') || minInputHasValue) {
      minInput.value = minValue;
    }

    if ((updateTextInputs && activeRangeType === 'max') || maxInputHasValue) {
      maxInput.value = maxValue;
    }
  }

  getFilterUrl() {
    const currentUrl = new URL(window.location.href);
    const url = new URL(window.location.pathname, window.location.origin);
    const currentSort = currentUrl.searchParams.get('sort_by');

    preserveSearchParams(currentUrl, url);

    if (currentSort) {
      url.searchParams.set('sort_by', currentSort);
    }

    this.querySelectorAll('input').forEach((input) => {
      if (input.disabled || !input.name) return;

      if ((input.type === 'checkbox' || input.type === 'radio') && !input.checked) return;
      if (input.type !== 'checkbox' && input.type !== 'radio' && !input.value.trim()) return;

      url.searchParams.append(input.name, input.value.trim());
    });

    return url;
  }

  async updateCollection(url = this.getFilterUrl()) {

    setProductListLoading(true);

    try {
      const doc = await fetchCollectionDoc(url, 'Failed to filter collection');

      updateCollectionCount(doc);
      replaceProductList(doc);
      dispatchCollectionRender({ doc, source: 'filters' });
      this.closeDialog();
      window.history.pushState({}, '', url.toString());
    } catch (error) {
      console.error(error);
      setProductListLoading(false);
    }
  }

  updateFromDoc(doc) {
    const isDialogFilters = this.isInDialog();
    const newFilters = isDialogFilters
      ? doc.querySelector('#collection-filters-dialog collection-filters')
      : doc.querySelector('.collection-main-left collection-filters');

    if (!newFilters) return;

    this.outerHTML = newFilters.outerHTML;
    const updatedFilters = isDialogFilters
      ? document.querySelector('#collection-filters-dialog collection-filters')
      : document.querySelector('.collection-main-left collection-filters');

    if (isDialogFilters) {
      updatedFilters?.updateDialogFooter(doc);
    }

    updatedFilters?.announceResults(doc);
  }

  updateDialogFooter(doc) {
    const dialog = this.closest('#collection-filters-dialog');
    const newDialog = doc.querySelector('#collection-filters-dialog');

    if (!dialog || !newDialog) return;

    const currentViewResultsButton = dialog.querySelector('[data-btn-view-results]');
    const newViewResultsButton = newDialog.querySelector('[data-btn-view-results]');

    if (currentViewResultsButton && newViewResultsButton) {
      currentViewResultsButton.innerHTML = newViewResultsButton.innerHTML;
      currentViewResultsButton.setAttribute(
        'aria-label',
        newViewResultsButton.getAttribute('aria-label') || newViewResultsButton.textContent.trim()
      );
    }
  }

  announceResults(doc) {
    const liveRegion = this.querySelector('[data-collection-live-region]');
    const newLiveRegion = doc.querySelector('[data-collection-live-region]');
    const countText = newLiveRegion?.dataset.countText;

    if (!liveRegion || !countText) return;

    const template = liveRegion.dataset.text || '[count]';
    const message = template.replace('[count]', countText.trim());

    clearTimeout(this.announcementTimer);
    clearTimeout(this.clearAnnouncementTimer);
    liveRegion.textContent = '';

    this.announcementTimer = setTimeout(() => {
      liveRegion.textContent = message;

      this.clearAnnouncementTimer = setTimeout(() => {
        liveRegion.textContent = '';
      }, 2000);
    }, 300);
  }
}
customElements.define('collection-filters', CollectionFilters);

class CollectionSortBy extends HTMLElement {
  constructor() {
    super();
    this.handleChange = this.handleChange.bind(this);
    this.handleCollectionRender = this.handleCollectionRender.bind(this);
  }

  connectedCallback() {
    this.addEventListener('change', this.handleChange);
    document.addEventListener(collectionRenderEvent, this.handleCollectionRender);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.handleChange);
    document.removeEventListener(collectionRenderEvent, this.handleCollectionRender);
  }

  handleChange(event) {
    const target = event.target;

    if (!(target instanceof HTMLInputElement)) return;
    if (target.type !== 'radio') return;

    this.sortCollection(target.value);
  }

  handleCollectionRender(event) {
    if (!event.detail.sortValue) return;

    this.updateSortInputs(event.detail.sortValue);
  }

  getSortUrl(sortValue) {
    const url = new URL(window.location.href);

    url.searchParams.set('sort_by', sortValue);
    url.searchParams.delete('page');

    return url;
  }

  async sortCollection(sortValue) {
    const url = this.getSortUrl(sortValue);

    setProductListLoading(true);

    try {
      const doc = await fetchCollectionDoc(url, 'Failed to sort collection');

      updateCollectionCount(doc);
      replaceProductList(doc);
      dispatchCollectionRender({ doc, source: 'sort', sortValue });
      this.closeDropdown();
      window.history.pushState({}, '', url.toString());
    } catch (error) {
      console.error(error);
      setProductListLoading(false);
    }
  }

  updateSortInputs(sortValue) {
    document.querySelectorAll('collection-sort-by input[type="radio"]').forEach((input) => {
      input.checked = input.value === sortValue;
    });
  }

  closeDropdown() {
    this.closest('theme-dropdown')?.close();
  }
}
customElements.define('collection-sort-by', CollectionSortBy);

class CollectionInfinitePagination extends HTMLElement {
  constructor() {
    super();
    this.handleClick = this.handleClick.bind(this);
    this.handleIntersection = this.handleIntersection.bind(this);
    this.handleCollectionRender = this.handleCollectionRender.bind(this);
  }

  connectedCallback() {
    this.button = this.querySelector('.infinite-pagination-btn');
    this.status = this.querySelector('#infinite-pagination-status');
    document.addEventListener(collectionRenderEvent, this.handleCollectionRender);

    if (!this.button) return;

    this.button.addEventListener('click', this.handleClick);
    this.initObserver();
  }

  disconnectedCallback() {
    if (this.button) {
      this.button.removeEventListener('click', this.handleClick);
    }

    this.observer?.disconnect();
    document.removeEventListener(collectionRenderEvent, this.handleCollectionRender);
  }

  handleClick(event) {
    event.preventDefault();
    this.loadNextPage();
  }

  initObserver() {
    if (!('IntersectionObserver' in window)) return;

    this.observer = new IntersectionObserver(this.handleIntersection, {
      rootMargin: '0px 0px -100px 0px',
    });
    this.observer.observe(this);
  }

  handleIntersection(entries) {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;

      this.loadNextPage();
    });
  }

  handleCollectionRender(event) {
    if (event.detail.source === 'infinite-pagination') return;

    this.updateFromDoc(event.detail.doc);
  }

  async loadNextPage() {
    if (!this.button || this.button.getAttribute('aria-disabled') === 'true') return;

    const url = new URL(this.button.href);

    this.setLoadingState(true);
    this.observer?.disconnect();

    try {
      const response = await fetch(url.toString());

      if (!response.ok) {
        throw new Error(`Failed to load collection page: ${response.status}`);
      }

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      this.appendProducts(doc);
      this.announceLoadedItems();
      this.updateFromDoc(doc);

      window.history.pushState({}, '', url.toString());
      saveCollectionInfiniteState(url.toString());
    } catch (error) {
      console.error(error);
      this.setLoadingState(false);
    }
  }

  setLoadingState(isLoading) {
    this.button.classList.toggle('loading', isLoading);
    this.button.setAttribute('aria-disabled', isLoading ? 'true' : 'false');
  }

  appendProducts(doc) {
    const currentProductList = document.querySelector('.product-list');
    const newProductItems = doc.querySelectorAll('.product-list .product-item');

    if (!currentProductList || !newProductItems.length) return;

    newProductItems.forEach((item) => {
      currentProductList.append(item);
    });

    dispatchCollectionProductsUpdated();
  }

  announceLoadedItems() {
    if (!this.status || !this.button.dataset.textStatus) return;

    this.status.textContent = this.button.dataset.textStatus;
  }

  updateFromDoc(doc) {
    const newPagination = doc.querySelector('collection-infinite-pagination');

    if (newPagination) {
      this.outerHTML = newPagination.outerHTML;
    } else {
      this.remove();
    }
  }
}
restoreCollectionInfiniteState();
customElements.define('collection-infinite-pagination', CollectionInfinitePagination);

window.addEventListener('pagehide', () => {
  saveCollectionInfiniteState();
});

class CollectionColsSelector extends HTMLElement {
  static storagePrefix = 'collection-cols-selector';
  static breakpoints = [
    {
      name: 'mobile',
      cssVar: '--cols-mobile',
      datasetKey: 'mobileCols',
      mediaQuery: '(max-width: 599px)',
      min: 1,
      max: 3,
    },
    {
      name: 'tablet',
      cssVar: '--cols-tablet',
      datasetKey: 'tabletCols',
      mediaQuery: '(min-width: 600px) and (max-width: 1199px)',
      min: 1,
      max: 4,
    },
    {
      name: 'desktop-sm',
      cssVar: '--cols-desktop-sm',
      datasetKey: 'desktopSmCols',
      mediaQuery: '(min-width: 1200px) and (max-width: 1599px)',
      min: 2,
      max: 5,
    },
    {
      name: 'desktop-lg',
      cssVar: '--cols-desktop-lg',
      datasetKey: 'desktopLgCols',
      mediaQuery: '(min-width: 1600px)',
      min: 2,
      max: 6,
    },
  ];

  constructor() {
    super();
    this.handleInput = this.handleInput.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.handleCollectionProductsUpdated = this.handleCollectionProductsUpdated.bind(this);
  }

  connectedCallback() {
    this.input = this.querySelector('input[type="range"]');
    this.text = this.querySelector('p');

    if (!this.input) return;

    this.input.addEventListener('input', this.handleInput);
    window.addEventListener('resize', this.handleResize);
    document.addEventListener(collectionProductsUpdatedEvent, this.handleCollectionProductsUpdated);
    this.updateInput();
    this.updateCols();
  }

  disconnectedCallback() {
    if (!this.input) return;

    this.input.removeEventListener('input', this.handleInput);
    window.removeEventListener('resize', this.handleResize);
    document.removeEventListener(collectionProductsUpdatedEvent, this.handleCollectionProductsUpdated);
  }

  handleInput() {
    this.saveCols();
    this.updateCols();
    this.updateText();
  }

  handleResize() {
    this.updateInput();
    this.updateText();
  }

  handleCollectionProductsUpdated() {
    this.updateCols();
  }

  getActiveBreakpoint() {
    return CollectionColsSelector.breakpoints.find((breakpoint) =>
      window.matchMedia(breakpoint.mediaQuery).matches
    ) || CollectionColsSelector.breakpoints[CollectionColsSelector.breakpoints.length - 1];
  }

  getStorageKey(breakpoint) {
    return `${CollectionColsSelector.storagePrefix}-${breakpoint.name}`;
  }

  getDefaultCols(breakpoint) {
    return this.dataset[breakpoint.datasetKey] || this.input.value || this.input.min;
  }

  updateInput() {
    const breakpoint = this.getActiveBreakpoint();
    const cols = this.getSavedCols(breakpoint) || this.getDefaultCols(breakpoint);

    this.input.min = breakpoint.min;
    this.input.max = breakpoint.max;
    this.input.value = cols;
    this.updateText();
  }

  getSavedCols(breakpoint) {
    try {
      const cols = window.localStorage.getItem(this.getStorageKey(breakpoint));

      if (!cols) return null;

      if (!this.isValidCols(cols, breakpoint)) return null;

      return cols;
    } catch {
      return null;
    }
  }

  isValidCols(cols, breakpoint) {
    const value = Number(cols);
    const min = Number(breakpoint.min);
    const max = Number(breakpoint.max);

    return !Number.isNaN(value) && value >= min && value <= max;
  }

  saveCols() {
    const breakpoint = this.getActiveBreakpoint();

    try {
      window.localStorage.setItem(this.getStorageKey(breakpoint), this.input.value);
    } catch {
      // Ignore storage failures so the selector still works for the current page.
    }
  }

  updateCols() {
    if (!this.input) return;

    const productGrid = document.querySelector('.product-list');

    CollectionColsSelector.breakpoints.forEach((breakpoint) => {
      const cols = this.getSavedCols(breakpoint) || this.getDefaultCols(breakpoint);

      productGrid?.style.setProperty(breakpoint.cssVar, cols);
    });

    requestCollectionProductAnimationIndexUpdate();
  }

  updateText() {
    if (this.text) {
      const text = Number(this.input.value) === 1
        ? this.dataset.textSingle
        : this.dataset.textMultiple;

      this.text.textContent = `${this.input.value} ${text}`;
    }
  }

}
customElements.define('collection-cols-selector', CollectionColsSelector);
